import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, ExpressionOperator, FilterOption, LoaderService, PatientRepository, SearchFields, SearchItems, SearchOptions, SearchResult, SearchSortOptions } from '../core';


/**
 * This Service handles patient and plan list.
 */
@Injectable({ providedIn: 'root' })
export class ListService {

	private readonly loaderSrv = inject(LoaderService);
	private readonly patRepo = inject(PatientRepository);

	private readonly _collapsed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	private _advancedSearch = false;
	private readonly _searchResult: BehaviorSubject<SearchResult<SearchItems>> = new BehaviorSubject<SearchResult<SearchItems>>({ items: [], count: 0 });
	private readonly _advancedSearchResult: BehaviorSubject<SearchResult<SearchItems[]>> = new BehaviorSubject<SearchResult<SearchItems[]>>({ items: [], count: 0 });
	private _searchOptions: SearchOptions = {
		page: 0,
		top: 10,
		patientOnly: false,
		deleted: false,
		filters: [],
		sortOrder: null
	};

	/**
	 * Update collapsed status of filters.
	 */
	collapse(val: boolean): void {
		this._collapsed.next(val);
	}

	/**
	 * Check if filters are collapsed.
	 */
	get isCollapsed(): Observable<boolean> {
		return this._collapsed.asObservable();
	}

	/**
	* Get current search result.
	*/
	getSearchResult(): Observable<SearchResult<SearchItems>> {
		return this._searchResult.asObservable();
	}

	/**
	* Get current advanced search result.
	*/
	getAdvancedSearchResult(): Observable<SearchResult<SearchItems[]>> {
		return this._advancedSearchResult.asObservable();
	}

	/**
	* Get current search result count.
	*/
	getSearchResultCount(): Observable<number> {
		const result: BehaviorSubject<SearchResult<any>> = this._advancedSearch ? this._advancedSearchResult : this._searchResult;
		return result.asObservable().pipe(map(res => res.count));
	}

	/**
	* Get current sort order option.
	*/
	getSortOrder(): SearchSortOptions | null {
		return this._searchOptions.sortOrder;
	}

	/**
	* Get current page number option.
	*/
	getPageNumber(): number {
		return this._searchOptions.page;
	}

	/**
	* Get current page size option.
	*/
	getPageSize(): number {
		return this._searchOptions.top;
	}

	/**
	* Set single filter.
	* @param {SearchFields} field Field of filter
	* @param {any} value Value of filter
	* @param {ExpressionOperator} operator Operator of filter
	*/
	setFilter(field: SearchFields, value: any, operator: ExpressionOperator) {
		const otherFilters = Object.assign([], this._searchOptions.filters!.filter(filter => filter.name != field));
		if (value) {
			otherFilters.push({ name: field, operator: operator, value: value });
		}
		this._searchOptions.filters = otherFilters;
	}

	/**
	* Set range filter.
	* @param {SearchFields} field Field of filter
	* @param {any} leftValue Value of left range
	* @param {ExpressionOperator} leftOperator Operator of left range
	* @param {any} rightValue Value of right range
	* @param {ExpressionOperator} rightOperator Operator of right range
	*/
	setRangeFilter(field: SearchFields, leftValue: any, leftOperator: ExpressionOperator, rightValue: any, rightOperator: ExpressionOperator) {
		const otherFilters = Object.assign([], this._searchOptions.filters!.filter(filter => filter.name != field));
		if (leftValue || leftValue === 0) {
			otherFilters.push({ name: field, operator: leftOperator, value: leftValue });
		}
		if (rightValue || rightValue === 0) {
			otherFilters.push({ name: field, operator: rightOperator, value: rightValue });
		}
		this._searchOptions.filters = otherFilters;
	}

	/**
	* Set deleted flag option.
	*/
	setDeletedFilter(value: boolean): void {
		this._searchOptions.deleted = value;
	}

	/**
	* Sort list.
	* @param {string} column Column to sort
	* @param {string} direction order to sort, i.e asc or desc
	*/
	sort(column: string, direction: string) {
		if (direction == 'desc') {
			this._searchOptions.sortOrder = { name: column, desc: true };
		} else if (direction == 'asc') {
			this._searchOptions.sortOrder = { name: column, desc: false };
		} else {
			this._searchOptions.sortOrder = null;
		}
		this.search();
	}

	/**
	* Update list by page number.
	*/
	changePageNumber(page: number) {
		this._searchOptions.page = page;
		this.search();
	}

	/**
	* Update list by page size.
	*/
	changePageSize(size: number) {
		this._searchOptions.page = 0;
		this._searchOptions.top = size;
		this.search();
	}

	/**
	* Update list.
	*/
	search(): void {
		this.loaderSrv.show();
		if (this._advancedSearch) {
			this.patRepo.advancedSearch(this._searchOptions).pipe(
				finalize(() => this.loaderSrv.hide()),
				map(res => this.handleApiResponse(res)),
			).subscribe(res => this._advancedSearchResult.next(res));
		} else {
			this.patRepo.search(this._searchOptions).pipe(
				finalize(() => this.loaderSrv.hide()),
				map(res => this.handleApiResponse(res))
			).subscribe(res => this._searchResult.next(res));
		}
	}

	private handleApiResponse<T>(response: ApiResponse<T>) {
		if (response.statusCode == ApiResponseStatusCode.Success) {
			return response.result as T;
		} else {
			throw new Error("Generic error");
		}
	}

	readonly PATIENT_DEFAULT_SORT = {
		name: SearchFields.patModifiedDate,
		desc: true
	};

	/**
	* Reset advanced patient and plans list.
	*/
	resetAdvancecSearch(): void {
		this._advancedSearch = true;
		this.removeReceivedFilter();
		const excludeReceived: FilterOption = { name: SearchFields.patIsReceived, value: true, operator: ExpressionOperator.Ne };
		const currentFilters = Object.assign([], [...this._searchOptions.filters!, excludeReceived]);
		const currentDeleted = this._searchOptions.deleted;
		this._searchOptions = {
			page: 0,
			top: 10,
			filters: currentFilters,
			deleted: currentDeleted,
			sortOrder: { ...this.PATIENT_DEFAULT_SORT },
			patientOnly: false
		};
		this.search();
	}

	readonly PLAN_DEFAULT_SORT = {
		name: SearchFields.planModifiedDate,
		desc: true
	};

	/**
	* Reset plan list.
	*/
	resetPlanSearch(): void {
		this._advancedSearch = false;
		this.removeReceivedFilter();
		const excludeReceived: FilterOption = { name: SearchFields.planIsReceived, value: true, operator: ExpressionOperator.Ne };
		const currentFilters = Object.assign([], [...this._searchOptions.filters!, excludeReceived]);
		const currentDeleted = this._searchOptions.deleted;
		this._searchOptions = {
			page: 0,
			top: 10,
			filters: currentFilters,
			deleted: currentDeleted,
			sortOrder: { ...this.PLAN_DEFAULT_SORT },
			patientOnly: false
		};
		this.search();
	}

	/**
	* Reset received list.
	*/
	resetReceivedSearch(): void {
		this._advancedSearch = false;
		this.removeReceivedFilter();
		const onlyReceived: FilterOption = { name: SearchFields.planIsReceived, value: true, operator: ExpressionOperator.Eq };
		const currentFilters = Object.assign([], [...this._searchOptions.filters!, onlyReceived]);
		const currentDeleted = this._searchOptions.deleted;
		this._searchOptions = {
			page: 0,
			top: 10,
			filters: currentFilters,
			deleted: currentDeleted,
			sortOrder: { ...this.PLAN_DEFAULT_SORT },
			patientOnly: false
		};
		this.search();
	}

	/**
	* Reset filters option.
	*/
	resetFilters(): void {
		const onlyReceived = this._searchOptions.filters!.filter(f => f.name === SearchFields.patIsReceived || f.name === SearchFields.planIsReceived);
		this._searchOptions.filters = Object.assign([], [...onlyReceived]);
		this._searchOptions.deleted = null;
	}

	/**
	* Reset search result.
	*/
	resetSearchResult(): void {
		this._searchResult.next({ items: [], count: 0 });
		this._advancedSearchResult.next({ items: [], count: 0 });
	}

	/**
	* Remove received filter.
	*/
	private removeReceivedFilter(): void {
		if (this._searchOptions.filters && this._searchOptions.filters.length > 0) {
			const currentFilters = this._searchOptions.filters.filter(f => f.name !== SearchFields.patIsReceived && f.name !== SearchFields.planIsReceived);
			this._searchOptions.filters = Object.assign([], currentFilters);
		}
	}

}
